import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { ArrowLeft, LoaderCircle, Save } from "lucide-react";
import { Button } from "../ui/button";
import { FieldError } from "../ui/field";
import { Input } from "../ui/input";
import ImageUploadGuidance from "../common/ImageUploadGuidance";
import InlineFormFileUpload, { IMAGE_TYPES } from "../file-upload/InlineFormFileUpload";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useAuthStore } from "../../context/AuthContext";
import { hasPermission } from "../../lib/utils/permissions";
import useToast from "../../hooks/useToast";
import apiClient from "../../lib/utils/network-client";
import SeoFields from "../seo/SeoFields";

const EMPTY_FORM = {
  title: "",
  slug: "",
  description: "",
  author_name: "",
  author_designation: "",
  blog_date: "",
  category: "",
  status: "draft",
  thumbnail_image: null,
  detail_image: null,
  meta_title: "",
  meta_description: "",
};

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link", "blockquote"],
    ["clean"],
  ],
};

const isMeaningfulHtml = (html) =>
  html && html.replace(/<(.|\n)*?>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;

const BlogFormPage = ({ mode }) => {
  const navigate = useNavigate();
  const { blogId } = useParams();
  const { user } = useAuthStore();
  const { showErrorToast, showSuccessToast } = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [isPageLoading, setIsPageLoading] = useState(mode === "edit");
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingContentMedia, setIsUploadingContentMedia] = useState(false);
  const [contentMediaFiles, setContentMediaFiles] = useState([]);
  const [thumbnailPreviewUrl, setThumbnailPreviewUrl] = useState("");
  const [detailPreviewUrl, setDetailPreviewUrl] = useState("");
  const quillRef = useRef(null);

  const isEditMode = mode === "edit";
  const isAdmin = hasPermission(user, "blogs", isEditMode ? "edit" : "create");

  const pageTitle = useMemo(
    () => (isEditMode ? "Edit Blog" : "Create Blog"),
    [isEditMode],
  );

  useEffect(() => {
    if (!isEditMode || !isAdmin) {
      setIsPageLoading(false);
      return;
    }

    const fetchBlog = async () => {
      setIsPageLoading(true);
      try {
        const response = await apiClient.get(`/blogs/${blogId}`);
        const blog = response.data?.data;

        if (!blog) {
          showErrorToast("Blog not found");
          navigate("/blogs");
          return;
        }

        setForm({
          title: blog.title || "",
          slug: blog.slug || "",
          description: blog.description || "",
          author_name: blog.author_name || "",
          author_designation: blog.author_designation || "",
          blog_date: blog.blog_date ? String(blog.blog_date).slice(0, 10) : "",
          category: blog.category || "",
          status: blog.status || "draft",
          thumbnail_image: null,
          detail_image: null,
          meta_title: blog.meta_title || "",
          meta_description: blog.meta_description || "",
        });
        setThumbnailPreviewUrl(blog.thumbnail_image_url || "");
        setDetailPreviewUrl(blog.detail_image_url || "");
      } catch (err) {
        showErrorToast(err.response?.data?.message || "Failed to load blog");
        navigate("/blogs");
      } finally {
        setIsPageLoading(false);
      }
    };

    fetchBlog();
  }, [blogId, isAdmin, isEditMode, navigate, showErrorToast]);

  const handleInputChange = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
    setFormErrors((current) => ({
      ...current,
      [field]: "",
    }));
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.title.trim()) nextErrors.title = "Blog title is required.";
    if (!isMeaningfulHtml(form.description)) {
      nextErrors.description = "Blog description is required.";
    }
    if (!form.author_name.trim()) {
      nextErrors.author_name = "Author name is required.";
    }
    if (!form.author_designation.trim()) {
      nextErrors.author_designation = "Author designation is required.";
    }
    if (!form.blog_date) nextErrors.blog_date = "Blog date is required.";
    if (!isEditMode && !form.thumbnail_image) {
      nextErrors.thumbnail_image = "Thumbnail image is required.";
    }
    if (!isEditMode && !form.detail_image) {
      nextErrors.detail_image = "Detail page image is required.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const createFormData = () => {
    const payload = new FormData();
    payload.append("title", form.title.trim());
    if (form.slug.trim()) {
      payload.append("slug", form.slug.trim());
    }
    payload.append("description", form.description);
    payload.append("author_name", form.author_name.trim());
    payload.append("author_designation", form.author_designation.trim());
    payload.append("blog_date", form.blog_date);
    payload.append("category", form.category.trim());
    payload.append("status", form.status);
    payload.append("meta_title", form.meta_title.trim());
    payload.append("meta_description", form.meta_description.trim());

    if (form.thumbnail_image) {
      payload.append("thumbnail_image", form.thumbnail_image);
    }

    if (form.detail_image) {
      payload.append("detail_image", form.detail_image);
    }

    return payload;
  };

  const handleContentMediaUpload = async (files) => {
    const selectedFiles = Array.isArray(files) ? files : files ? [files] : [];
    setContentMediaFiles(selectedFiles);

    if (selectedFiles.length === 0) return;

    const payload = new FormData();
    selectedFiles.forEach((file) => payload.append("files", file));

    setIsUploadingContentMedia(true);
    try {
      const response = await apiClient.post("/blogs/content-assets", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploadedFiles = response.data?.data?.files || [];
      const editor = quillRef.current?.getEditor?.();

      if (!editor || uploadedFiles.length === 0) {
        throw new Error("The uploaded media could not be inserted into the editor.");
      }

      const selection = editor.getSelection(true);
      let insertAt = selection?.index ?? Math.max(0, editor.getLength() - 1);

      uploadedFiles.forEach((file) => {
        if (file.mime_type?.startsWith("image/")) {
          editor.insertEmbed(insertAt, "image", file.url, "user");
          insertAt += 1;
        } else {
          const label = file.original_name || "Download PDF";
          editor.insertText(insertAt, label, { link: file.url }, "user");
          insertAt += label.length;
        }

        editor.insertText(insertAt, "\n", "user");
        insertAt += 1;
      });

      editor.setSelection(insertAt, 0, "silent");
      showSuccessToast(
        `${uploadedFiles.length} file${uploadedFiles.length === 1 ? "" : "s"} uploaded to the server and inserted.`,
      );
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || err.message || "Unable to upload blog media",
      );
    } finally {
      setIsUploadingContentMedia(false);
      setContentMediaFiles([]);
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSaving(true);
    try {
      const payload = createFormData();

      if (isEditMode) {
        await apiClient.put(`/blogs/${blogId}`, payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Blog updated successfully");
      } else {
        await apiClient.post("/blogs", payload, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessToast("Blog created successfully");
      }

      navigate("/blogs");
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Unable to save blog");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>
        <p className="mt-2 text-sm text-slate-500">
          Blog management is only available for admin users.
        </p>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div className="rounded-lg border border-white/60 bg-white/80 p-8 shadow-sm">
        <div className="flex items-center gap-3 text-sm font-medium text-slate-500">
          <LoaderCircle className="h-4 w-4 animate-spin" />
          Loading blog editor...
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Button
            type="button"
            variant="ghost"
            className="-ml-3 mb-2 rounded-lg px-3 text-slate-500"
            onClick={() => navigate("/blogs")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Blogs
          </Button>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">
            {pageTitle}
          </h1>
          <p className="mt-1 text-sm font-medium text-slate-500">
            {isEditMode
              ? "Update blog content, images, and author details."
              : "Create a blog with rich content, images, and author details."}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSave}
        className="rounded-lg border border-white/60 bg-white/80 p-6 shadow-sm backdrop-blur-xl"
      >
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Title</label>
              <Input
                value={form.title}
                onChange={(event) => handleInputChange("title", event.target.value)}
                placeholder="GCS Hospital launches new specialty clinic"
                className={`rounded-lg ${formErrors.title ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <FieldError>{formErrors.title}</FieldError>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Custom Slug
                </label>
                <Input
                  value={form.slug}
                  onChange={(event) => handleInputChange("slug", event.target.value)}
                  placeholder="optional-custom-slug"
                  className="rounded-lg"
                />
                <p className="text-xs text-slate-500">
                  Leave blank to auto-generate on create. Existing slugs stay stable unless you change this field.
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Category
                </label>
                <Input
                  value={form.category}
                  onChange={(event) =>
                    handleInputChange("category", event.target.value)
                  }
                  placeholder="e.g. Medical Research"
                  className="rounded-lg"
                />
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Author Name
                </label>
                <Input
                  value={form.author_name}
                  onChange={(event) =>
                    handleInputChange("author_name", event.target.value)
                  }
                  placeholder="Dr. Ananya Sharma"
                  className={`rounded-lg ${formErrors.author_name ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                />
                <FieldError>{formErrors.author_name}</FieldError>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Author Designation
                </label>
                <Input
                  value={form.author_designation}
                  onChange={(event) =>
                    handleInputChange("author_designation", event.target.value)
                  }
                  placeholder="Senior Cardiologist"
                  className={`rounded-lg ${formErrors.author_designation ? "border-red-300 focus-visible:ring-red-500" : ""}`}
                />
                <FieldError>{formErrors.author_designation}</FieldError>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Date</label>
                <Input
                  type="date"
                  value={form.blog_date}
                  onChange={(event) =>
                    handleInputChange("blog_date", event.target.value)
                }
                className={`rounded-lg ${formErrors.blog_date ? "border-red-300 focus-visible:ring-red-500" : ""}`}
              />
              <FieldError>{formErrors.blog_date}</FieldError>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Status</label>
              <Select value={form.status} onValueChange={(value) => handleInputChange("status", value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">
                Description
              </label>
              <InlineFormFileUpload
                label="Description Image or PDF"
                value={contentMediaFiles}
                onChange={handleContentMediaUpload}
                accept={[...IMAGE_TYPES, "application/pdf"]}
                maxFiles={20}
                maxSizeBytes={10 * 1024 * 1024}
                disabled={isUploadingContentMedia}
                helperText="JPG, PNG, GIF, WEBP or PDF — maximum 10 MB per file"
                labels={{
                  chooseFile: isUploadingContentMedia ? "Uploading..." : "Choose Files",
                  readyToSave: isUploadingContentMedia
                    ? "Uploading and inserting into the description..."
                    : "Ready to upload",
                }}
              />
              <div
                className={`overflow-hidden rounded-lg border bg-white ${
                  formErrors.description ? "border-red-300" : "border-slate-200"
                }`}
              >
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={form.description}
                  onChange={(value) => handleInputChange("description", value)}
                  modules={quillModules}
                  placeholder="Write the full blog content here..."
                />
              </div>
              <FieldError>{formErrors.description}</FieldError>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <InlineFormFileUpload
                label="Thumbnail Image"
                value={form.thumbnail_image}
                onChange={(file) => handleInputChange("thumbnail_image", file)}
                existingFiles={thumbnailPreviewUrl ? [thumbnailPreviewUrl] : []}
                accept={IMAGE_TYPES}
                maxSizeBytes={5 * 1024 * 1024}
                required={!isEditMode}
                error={formErrors.thumbnail_image}
                helperText="JPG, PNG, GIF or WEBP — maximum 5 MB"
              />
              <ImageUploadGuidance
                requirementKey="blogThumbnail"
                file={form.thumbnail_image}
                src={thumbnailPreviewUrl}
              />
            </div>

            <div className="space-y-2">
              <InlineFormFileUpload
                label="Detail Page Image"
                value={form.detail_image}
                onChange={(file) => handleInputChange("detail_image", file)}
                existingFiles={detailPreviewUrl ? [detailPreviewUrl] : []}
                accept={IMAGE_TYPES}
                maxSizeBytes={5 * 1024 * 1024}
                required={!isEditMode}
                error={formErrors.detail_image}
                helperText="JPG, PNG, GIF or WEBP — maximum 5 MB"
              />
              <ImageUploadGuidance
                requirementKey="blogDetail"
                file={form.detail_image}
                src={detailPreviewUrl}
              />
            </div>
          </div>
        </div>

        <SeoFields
          metaTitle={form.meta_title}
          metaDescription={form.meta_description}
          onChange={handleInputChange}
          className="mt-6"
        />

        <div className="mt-6 flex flex-col gap-3 border-t border-slate-100 pt-5 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            className="rounded-lg"
            onClick={() => navigate("/blogs")}
          >
            Cancel
          </Button>
          <Button type="submit" className="rounded-lg" disabled={isSaving}>
            {isSaving ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isSaving
              ? "Saving..."
              : isEditMode
                ? "Update Blog"
                : "Create Blog"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default BlogFormPage;
